import React from 'react';
import { Flex, Text, Tooltip } from '@chakra-ui/react';

type Properties = {
  children: React.ReactNode;
  height?: string | number;
  onError?: (error: unknown, errorInfo: React.ErrorInfo) => void;
};

type State = {
  hasError: boolean;
  message?: string;
};

class InstrumentErrorBoundary extends React.Component<Properties, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : 'An error occurred',
    };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Instrument render crashed:', error);
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Chakra's polymorphic component typing can blow up in some TS configs.
      // Casting here keeps the boundary simple and avoids "union too complex".
      const FlexContainer: any = Flex;
      const TooltipContainer: any = Tooltip;
      const TextContainer: any = Text;
      const errorContainerProperties: any = {
        height: this.props.height ?? '100%',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 1,
        px: 3,
      };
      return (
        <FlexContainer {...errorContainerProperties}>
          <TooltipContainer label={this.state.message || 'An error occurred'} placement='top'>
            <TextContainer
              fontSize='sm'
              color='red.400'
              textAlign='center'
              cursor='default'
              userSelect='none'
            >
              ⚠ Something went wrong
            </TextContainer>
          </TooltipContainer>
        </FlexContainer>
      );
    }

    return this.props.children;
  }
}

export default InstrumentErrorBoundary;
